import { FC, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { ViewActivityResponse } from "../../../../../../api/services/programTemplate/models";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiTypography,
} from "../../../../../../components";
import MuiChip from "../../../../../../components/chip/MuiChip";
import { MuiDoubleLabelWithChip } from "../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import { getValueOrBlank, isNotEmpty } from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { userPreferredLanguageSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { getUserPreferredLanguageTopicValue } from "../../../../modelsAndSchemas/utils";

interface ViewActivityProps {
  activityData: ViewActivityResponse;
  frameworkName: string;
  onViewManageLocal: () => void;
}

export const ViewActivityGeneral: FC<ViewActivityProps> = ({
  activityData,
  frameworkName,
  onViewManageLocal,
}) => {
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const modalityRef = useRef<HTMLDivElement>(null);
  const candidateStatusRef = useRef<HTMLDivElement>(null);
  const deliverablesRef = useRef<HTMLDivElement>(null);

  const gridSize = { md: 4, sm: 6, xs: 12 };
  const { fetchValueByKeyOfDropdown, fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const activityStatusLabel = fetchValueByKeyOfDropdown(
    createProgramSiteCoreKeys.createActivityKey,
    createProgramSiteCoreKeys.activityStatusList,
    activityData?.activityStatus ?? "",
  );

  const ariaLabel = commonARIALabel.showNMore;
  const renderChip = (value: string) => {
    return (
      <MuiChip
        data={[
          {
            value: value,
            label: fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              value,
            ),
          },
        ]}
        showMoreARIALabel={ariaLabel}
        handleDelete={() => {}}
        isEditable={false}
        removeChipNameARIALabel={commonARIALabel.removeChipName}
      />
    );
  };

  const deliverablesOption = useMemo(() => {
    return Array.isArray(activityData?.deliverables)
      ? activityData.deliverables.map((item) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [activityData]);
  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="activity-tab-general"
        className="activity-tab-general"
      >
        {isNotEmpty(frameworkName) && (
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.activityProgramFramework,
              )}
              value={frameworkName}
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize}>
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.participantStatus,
            )}
            replaceEmptyWithDash={!activityData?.candidateStatus?.length}
            value={activityData?.candidateStatus?.map((item: string) => ({
              label: fetchValueByKeyOfDropdown(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.candidateStatusList,
                item,
              ),
              value: item,
            }))}
            chipPopoverHeaderLabel={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.participantStatus,
            )}
            chipPopoverGridRef={candidateStatusRef}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.activityType,
            )}
            value={getValueOrBlank(activityData?.activityName)}
            replaceEmptyWithDash
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.modality,
            )}
            replaceEmptyWithDash={!activityData?.modality?.length}
            value={activityData?.modality?.map((item: string) => ({
              label: fetchValueByKeyOfDropdown(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.modalityList,
                item,
              ),
              value: item,
            }))}
            chipPopoverGridRef={modalityRef}
            chipPopoverHeaderLabel={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.modality,
            )}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.topic,
            )}
            value={getUserPreferredLanguageTopicValue(
              activityData,
              userSelectedLanguage,
            )}
            isValueWithEllipsis
            valueClasses="topic-value"
          />
          <MuiLink className="view-locale-link" onClick={onViewManageLocal}>
            {commonLabels.viewLocale}
          </MuiLink>
        </MuiGrid>
        <MuiGrid size={gridSize} className="deliverables-dropdown">
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.deliverables,
            )}
            replaceEmptyWithDash={!deliverablesOption.length}
            value={deliverablesOption}
            chipPopoverHeaderLabel={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.selectedDeliverables,
            )}
            chipPopoverGridRef={deliverablesRef}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              createProgramSiteCoreKeys.createActivityKey,
              createProgramSiteCoreKeys.activityStatus,
            )}
            value={""}
          />
          <StatusBadge
            status={activityData?.activityStatus as StatusBadgeType}
            label={activityStatusLabel}
          />
        </MuiGrid>
      </MuiGrid>

      {(activityData?.triggerBilling ||
        activityData?.countsTowardsLastContactDate ||
        activityData?.triggerTouchPointSurvey) && (
        <>
          <MuiBox>
            <MuiTypography
              variant="h2Bold"
              className="select-applicable"
              component="h2"
            >
              {fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.selectedOptionsText,
              )}
            </MuiTypography>
          </MuiBox>
          <MuiGrid container className="checkbox-container">
            {activityData?.triggerBilling &&
              renderChip(createProgramSiteCoreKeys.triggerBilling)}
            {activityData?.countsTowardsLastContactDate &&
              renderChip(
                createProgramSiteCoreKeys.countsTowardsLastContactDate,
              )}
            {activityData?.triggerTouchPointSurvey &&
              renderChip(createProgramSiteCoreKeys.triggerTouchPointSurvey)}
          </MuiGrid>
        </>
      )}
    </MuiBox>
  );
};
